import {
	ChevronDownIcon,
	UserCircleIcon,
	XCircleIcon,
} from '@heroicons/react/24/solid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import isEmpty from 'lodash.isempty';
import isNull from 'lodash.isnull';
import isString from 'lodash.isstring';
import { useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useModuleStore } from '../../components/moduleStore';
import { useNotificationStore } from '../../components/notificationStore';
import { useUserStore } from '../../components/userStore';
import { formatBirthDate } from '../../components/util';
import constants from '../../constants';
import { useApplicationStore } from '../NewApplication/components/applicationStore';
import CloneRequestModal from './components/CloneRequestModal';
import CloseRequestModal from './components/CloseRequestModal';
import FundsModal from './components/FundsModal';
import InvoiceModal from './components/InvoiceModal';
import ModuleForm from './components/ModuleForm';
import ProfileModal from './components/ProfileModal';
import ReassignModal from './components/ReassignModal';
import ReferralHistory from './components/ReferralHistory';
import SKUModal from './components/SKUModal';
import WorkNotesForm from './components/WorkNotesForm';

const USDollar = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

const ApplicationModules = () => {
	const { user } = useUserStore();
	const { requestId } = useParams();
	let { serviceRequest = {} } = useModuleStore();
	let isFetching = false;
	let srIsFetched = false;
	const [selectedForm, setSelectedForm] = useState();
	const { showSuccess, showError } = useNotificationStore();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const modalRef = useRef();
	const [isReassignModalOpen, setReassignModalOpen] = useState(false);
	const [isCloneModalOpen, setCloneModalOpen] = useState(false);
	const [isCloseModalOpen, setCloseModalOpen] = useState(false);
	const [nextCloseStatus, setNextCloseStatus] = useState();
	const [isSKUModalOpen, setSKUModalOpen] = useState(false);
	const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
	const [isFundsModalOpen, setFundsModalOpen] = useState(false);
	const [isProfileModalOpen, setProfileModalOpen] = useState(false);
	const { setExistingClient } = useApplicationStore();

	({
		data: serviceRequest,
		isFetching,
		isFetched: srIsFetched,
	} = useQuery({
		queryKey: ['worklist', requestId],
		queryFn: async () => {
			const { data } = await axios.get(`/requests/worklist/${requestId}`);
			return data;
		},
		initialData: serviceRequest,
		enabled: !!requestId,
	}));

	if (!serviceRequest && !requestId) {
		return <Navigate to="/worklist" />;
	}

	const readOnly = constants.closedStatuses.includes(
		serviceRequest?.request_status,
	);
	const isReferringAgency =
		serviceRequest?.coord_agency_id === user?.coord_agency_id;
	const isReceivingAgency =
		serviceRequest.request_status !== 'incomplete' &&
		user.coord_agency_id ===
			serviceRequest?.external_211_service?.coord_agency_id;
	const isNotAssigned = !serviceRequest?.assigned_to_coord_id;
	const isEditable =
		!readOnly &&
		((isReferringAgency && serviceRequest?.request_status === 'incomplete') ||
			(isReceivingAgency &&
				(isNotAssigned || user.id === serviceRequest?.assigned_to_coord_id)));
	const canAssign =
		!readOnly && isReceivingAgency && !isNotAssigned && !isEditable;
	const isVerbalReferral = serviceRequest?.request_status === 'closed-verbal';

	const serviceForms = useQuery({
		queryKey: ['required-service-forms', requestId],
		queryFn: async () => {
			const { data } = await axios.get('/care_quest_groups', {
				params: {
					filter: JSON.stringify({
						where: {
							id: {
								inq: serviceRequest.requiredAssessments.map(
									({ care_quest_group_id }) => care_quest_group_id,
								),
							},
						},
						order: 'name',
					}),
				},
			});
			return data;
		},
		enabled:
			((isEditable && isReferringAgency) || isReceivingAgency) &&
			serviceRequest.isFetched &&
			serviceRequest?.data?.requiredAssessments?.length > 0,
	});

	const checkCoverageQuery = useQuery({
		queryKey: ['credentialed-service', serviceRequest.external_211_services_id],
		queryFn: async () => {
			const { data } = await axios.get(`/coord_agencies/credentialed-service`, {
				params: {
					external211ServicesId: serviceRequest.external_211_services_id,
				},
			});
			return data;
		},
		enabled: srIsFetched && !!serviceRequest.client.health_plan_name,
	});

	const availableSKUsQuery = useQuery({
		enabled: srIsFetched && isEditable,
		queryKey: ['available-skus', serviceRequest.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/requests/${serviceRequest.id}/get-available-skus`,
			);
			return data;
		},
	});

	const assignMutation = useMutation({
		mutationFn: async () => {
			await axios.post(`/requests/${requestId}/assign-to-me`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['dashboard'],
			});
			queryClient.invalidateQueries({
				queryKey: ['worklist'],
			});
			queryClient.invalidateQueries({ queryKey: ['worklist', requestId] });
			showSuccess('Assigned successfully');
		},
		onError: () => {
			showError('Unable to assign service request. Please try again.');
		},
	});

	const reassignMutation = useMutation({
		mutationFn: async (coordId) => {
			await axios.post(`/requests/${requestId}/reassign-to/${coordId}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['dashboard'],
			});
			queryClient.invalidateQueries({
				queryKey: ['worklist'],
			});
			queryClient.invalidateQueries({ queryKey: ['worklist', requestId] });
			showSuccess('Reassigned successfully');
		},
		onError: () => {
			showError('Unable to reassign service request. Please try again.');
		},
	});

	const statusMutation = useMutation({
		mutationFn: async (request_status) => {
			await axios.put(`/requests/${requestId}`, {
				request_status,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['worklist', requestId],
			});
			showSuccess('Status updated');
			// navigate('/worklist/incoming');
		},
		onError: () => {
			showError('Failed to update status');
		},
	});

	const appValueMutation = useMutation({
		mutationFn: async (app_dollar_value) => {
			await axios.put(`/requests/${requestId}`, {
				app_dollar_value,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['worklist', requestId],
			});
			showSuccess('Application value updated');
		},
		onError: () => {
			showError('Failed to update application value');
		},
	});

	const skuMutation = useMutation({
		mutationFn: async (taxonomy_skus) => {
			const skuIds = taxonomy_skus.map(({ id }) => id);
			const deletePromises = serviceRequest.taxonomy_skus
				.filter(({ id }) => !skuIds.includes(id))
				.map((sku) =>
					axios.delete(`/requests/${requestId}/taxonomy_skus/${sku.id}`),
				);
			await Promise.all(deletePromises);
			const addPromises = taxonomy_skus
				.filter(({ id }) => isString(id))
				.map(({ taxonomy_sku }) =>
					axios.post(`/requests/${requestId}/taxonomy_skus`, {
						taxonomy_sku_id: taxonomy_sku.id,
						request_id: requestId,
					}),
				);
			await Promise.all(addPromises);
			// const updatePromises = taxonomy_skus
			// 	.filter(({ id }) => id)
			// 	.map(({ id, taxonomy_sku }) =>
			// 		axios.put(`/requests/${requestId}/taxonomy_skus/${id}`, {
			// 			taxonomy_sku_id: taxonomy_sku.id,
			// 			request_id: requestId,
			// 		}),
			// 	);
			// await Promise.all(updatePromises);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['worklist', requestId],
			});
			showSuccess('SKUs updated');
			setSKUModalOpen(false);
		},
		onError: () => {
			showError('Failed to update SKUs');
		},
	});

	const clientMutation = useMutation({
		mutationFn: async ([clientData, skipClose = false]) => {
			if (clientData.challengeResponses) {
				const { challengeResponses } = clientData;
				delete clientData.challengeResponses;
				await axios.post(`/requests/${requestId}/client`, clientData);
				await axios.post(
					`/requests/${requestId}/update-client-security-questions`,
					{
						answers: challengeResponses,
					},
				);
			} else {
				await axios.post(`/requests/${requestId}/client`, clientData);
			}
			return { skipClose };
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: ['worklist', requestId],
			});
			showSuccess('Client updated');
			if (!data?.skipClose) {
				setProfileModalOpen(false);
			}
		},
		onError: () => {
			showError('Failed to update client');
		},
	});

	const handleMenuClick = (event) => {
		event.preventDefault();
		const { formId } = event.target.dataset;
		setSelectedForm(formId);
	};

	const handleStatusChange = (event) => {
		event.preventDefault();
		statusMutation.mutate(event.target.dataset.toStatus);
		modalRef.current.close();
	};

	const handleRelease = () => {
		modalRef.current.showModal();
	};

	const handleOpenCloneModal = (event) => {
		event.preventDefault();
		setCloneModalOpen(true);
	};

	const handleClone = () => {
		setExistingClient(serviceRequest.client);
		setCloneModalOpen(false);
		navigate('/newApplication/clone');
	};

	const handleOpenCloseModal = (event) => {
		event.preventDefault();
		setNextCloseStatus(event.target.dataset.toStatus);
		setCloseModalOpen(true);
	};

	const handleClose = (closedReason) => {
		if (isEmpty(closedReason)) {
			statusMutation.mutate(nextCloseStatus);
		} else {
			statusMutation.mutate(closedReason);
		}
		setCloseModalOpen(false);
	};

	const handleAssignToMe = (event) => {
		event.preventDefault();
		assignMutation.mutate();
	};

	// patient coverage
	const isCovered =
		!!serviceRequest?.client?.health_plan_name &&
		checkCoverageQuery.isFetched &&
		checkCoverageQuery?.data?.length > 0 &&
		serviceRequest.reimbursable;

	const isInFulfillment = serviceRequest?.request_status === 'fulfillment';

	const clientAddress = [
		serviceRequest.client?.address,
		serviceRequest.client?.city,
		serviceRequest.client?.state,
		serviceRequest.client?.zip,
	]
		.join(' ')
		.trim();

	return (
		<>
			<div className="p-4 bg-white rounded">
				<div className="flex flex-row bg-white rounded">
					<div className="mt-4 ml-4 mr-8">
						<span className="inline-block w-40 h-40 overflow-hidden bg-gray-100 rounded-full">
							<UserCircleIcon className="w-full h-full text-gray-300" />
						</span>
					</div>
					<div className={cn('grow', { skeleton: isFetching })}>
						{!isFetching && (
							<>
								<div className="text-2xl text-primary">
									<strong>
										{serviceRequest.client.name_first}{' '}
										{!isEmpty(serviceRequest.client.nickname) &&
											serviceRequest.client.nickname}{' '}
										{serviceRequest.client.name_last}
									</strong>
									<span className="text-base text-gray-700"> applied for </span>
									<span className="text-xl">
										{serviceRequest.taxonomy_detail ||
											serviceRequest?.external_211_service?.data?.name}
									</span>
									<span className="text-base text-gray-700"> on </span>
									<span className="text-xl">
										{formatBirthDate(serviceRequest.request_dttm)}
									</span>
									{!readOnly && isEditable && (
										<button
											className="ml-4 btn btn-outline btn-primary btn-sm"
											onClick={() => setProfileModalOpen(true)}
										>
											Edit Profile
										</button>
									)}
									{!isEmpty(serviceRequest?.client.status) &&
										isEmpty(serviceRequest?.client.last_4_ssn) && (
											<div className="inline-block ml-2">
												<div
													className="tooltip tooltip-right"
													data-tip="No Tandem profile"
												>
													<XCircleIcon className="w-5 h-5 text-error" />
												</div>
											</div>
										)}
								</div>
								<div
									className={cn(
										'grid',
										'gap-2',
										{ 'grid-cols-3': !isCovered, 'grid-cols-4': isCovered },
										'mt-2',
									)}
								>
									<div>
										<dl>
											<dt>Address</dt>
											<dd>
												{!isEmpty(clientAddress) ? (
													clientAddress
												) : (
													<em>Not available</em>
												)}
											</dd>
											<dt>DOB</dt>
											<dd>
												{formatBirthDate(serviceRequest.client.birth_dttm)}
											</dd>
											<dt>Gender</dt>
											<dd>
												{!isEmpty(serviceRequest.client.gender) ? (
													serviceRequest.client.gender
												) : (
													<em>Not available</em>
												)}
											</dd>
											<dt>Email</dt>
											<dd>
												{!isEmpty(serviceRequest.client.email) ? (
													serviceRequest.client.email
												) : (
													<em>Not available</em>
												)}
											</dd>
											<dt>Mobile</dt>
											<dd>
												{!isEmpty(serviceRequest.client.phone_mobile) ? (
													serviceRequest.client.phone_mobile
												) : (
													<em>Not available</em>
												)}
											</dd>
										</dl>
									</div>
									<div>
										<dl>
											<dt>Race/Ethnicity</dt>
											<dd>
												{!isEmpty(serviceRequest.client.race) ? (
													constants.race[serviceRequest.client.race]
												) : (
													<em>Not available</em>
												)}
												/
												{!isEmpty(serviceRequest.client.ethnicity) ? (
													constants.ethnicity[serviceRequest.client.ethnicity]
												) : (
													<em>Not available</em>
												)}
											</dd>
											<dt>Veteran Status</dt>
											<dd>
												{!isEmpty(serviceRequest.client.veteran_status) ? (
													constants.veteran[
														serviceRequest.client.veteran_status
													]
												) : (
													<em>Not available</em>
												)}
											</dd>
											<dt>Disability</dt>
											<dd>
												{!isEmpty(serviceRequest.client.disability_status) ? (
													constants.disability[
														serviceRequest.client.disability_status
													]
												) : (
													<em>Not available</em>
												)}
											</dd>
										</dl>
									</div>
									<div>
										<dl>
											{isReferringAgency && (
												<>
													<dt>Receiving Org</dt>
													<dd>
														{
															serviceRequest?.external_211_service?.data
																.locations[0].organization_name
														}
													</dd>
												</>
											)}
											{isReceivingAgency && (
												<>
													<dt>Referring Org</dt>
													<dd>
														{isEmpty(serviceRequest?.coord_agency?.agency_name)
															? 'Self Referred'
															: serviceRequest?.coord_agency?.agency_name}
													</dd>
												</>
											)}
											<dt>Request ID</dt>
											<dd>{serviceRequest.id}</dd>
											{!isEmpty(serviceRequest.taxonomy_skus) &&
												serviceRequest.taxonomy_skus.every(
													(sku) => !isEmpty(sku.taxonomy_sku),
												) && (
													<>
														<dt>SKU(s)</dt>
														<dd>
															{serviceRequest.taxonomy_skus.map(
																({ id, taxonomy_sku }) =>
																	taxonomy_sku && (
																		<div key={`sku-link-${id}`}>
																			{taxonomy_sku.sku_number}{' '}
																			{taxonomy_sku.label}
																		</div>
																	),
															)}
														</dd>
													</>
												)}
										</dl>
									</div>
									{isCovered && (
										<div>
											<dl>
												<dt>Insurance Provider</dt>
												<dd>
													{
														checkCoverageQuery?.data?.[0]
															.ilos_credentialed_agency.healthplan_coord_agency
															.agency_name
													}
												</dd>
												<dt>Service Unit Cost</dt>
												<dd>
													{USDollar.format(
														checkCoverageQuery?.data?.[0].service_unit_cost,
													)}
												</dd>
												<dt>Service Unit Frequency</dt>
												<dd>
													{
														constants.durationUnits[
															checkCoverageQuery?.data?.[0].duration_unit
														]
													}
												</dd>
												<dt>Service Unit Duration:</dt>
												<dd>
													{checkCoverageQuery?.data?.[0].duration}{' '}
													{checkCoverageQuery?.data?.[0].duration_unit}
												</dd>
											</dl>
										</div>
									)}
								</div>
							</>
						)}
					</div>
					<div className="w-40 text-right">
						{readOnly && (
							<div className="text-2xl text-primary">
								<strong>
									{constants.status[serviceRequest.request_status]}
								</strong>
							</div>
						)}
						{isEditable &&
							isReferringAgency &&
							serviceRequest.request_status === 'incomplete' && (
								<>
									<div>
										<button
											onClick={handleRelease}
											className="btn-block btn btn-primary"
											title="Release to Referral Org"
										>
											Release
										</button>
									</div>
									<div>
										<button
											onClick={handleOpenCloneModal}
											className="mt-4 btn-block btn btn-outline btn-primary"
											title="Create new Service Request for this client"
										>
											Add Service
										</button>
									</div>
								</>
							)}
						{isReceivingAgency && isEditable && (
							<>
								<div className="w-full dropdown dropdown-bottom dropdown-end">
									<div
										tabIndex={0}
										role="button"
										className="m-1 btn-block btn btn-primary"
									>
										Actions
										<ChevronDownIcon
											aria-hidden="true"
											className="w-5 h-5 -mr-1"
										/>
									</div>
									<ul
										tabIndex={0}
										className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow"
									>
										{isNotAssigned && (
											<li>
												<a onClick={handleAssignToMe}>Assign to Me</a>
											</li>
										)}
										<li>
											<a onClick={() => setReassignModalOpen(true)}>Reassign</a>
										</li>
										{!isInFulfillment &&
											availableSKUsQuery.isFetched &&
											Array.isArray(availableSKUsQuery.data) &&
											availableSKUsQuery.data.length > 0 && (
												<>
													<li className="h-px m-0 divider" />
													<li>
														<a onClick={() => setSKUModalOpen(true)}>
															Edit SKUs
														</a>
													</li>
													<li>
														<a onClick={() => setFundsModalOpen(true)}>
															Link Funds
														</a>
													</li>
												</>
											)}
										{!isInFulfillment && (
											<>
												<li className="h-px m-0 divider" />
												<li>
													<a
														onClick={handleStatusChange}
														data-to-status="onhold"
													>
														On Hold
													</a>
												</li>
											</>
										)}
										{isInFulfillment && (
											<>
												<li className="h-px m-0 divider" />
												<li>
													<a onClick={() => setInvoiceModalOpen(true)}>
														Update Line Items
													</a>
												</li>
											</>
										)}
										<li className="h-px m-0 divider" />
										<li>
											<a
												onClick={handleOpenCloseModal}
												data-to-status="completed"
											>
												Service Provided
											</a>
										</li>
										<li>
											<a onClick={handleOpenCloseModal} data-to-status="closed">
												Service Not Provided
											</a>
										</li>
										{!isInFulfillment && (
											<>
												{!isNull(serviceRequest.taxonomy_sku_id) && (
													<>
														<li className="h-px m-0 divider" />
														<li>
															<a
																onClick={handleStatusChange}
																data-to-status="fulfillment"
															>
																In Fulfillment
															</a>
														</li>
													</>
												)}
												<li className="h-px m-0 divider" />
												<li>
													<a
														onClick={handleOpenCloneModal}
														title="Create new Service Request for this client"
													>
														Add Service
													</a>
												</li>
											</>
										)}
									</ul>
								</div>
								{isEditable && (
									<div className="w-full mt-4 form-control">
										<label
											className="label label-text"
											htmlFor="application-value"
										>
											Application Value
										</label>
										<label
											className="flex items-center gap-2 input input-sm input-bordered"
											htmlFor="application-value"
										>
											$
											<input
												type="number"
												className="grow max-w-28 bg-unset"
												id="application-value"
												defaultValue={serviceRequest.app_dollar_value}
												onBlur={({ target }) =>
													!readOnly && appValueMutation.mutate(target.value)
												}
												readOnly={readOnly}
											/>
										</label>
									</div>
								)}
							</>
						)}
						{canAssign && (
							<button
								onClick={handleAssignToMe}
								className="btn btn-primary btn-block"
								title="Assign to Me"
							>
								Assign to Me
							</button>
						)}
						{isVerbalReferral && (
							<Link
								to={`/worklist/application/${serviceRequest.id}/print`}
								target="_blank"
								rel="noreferrer"
								className="btn btn-primary btn-block"
								title="Print Service Details"
							>
								Print
							</Link>
						)}
					</div>
				</div>
				{isCovered && (
					<div className="p-4 mt-4 -mb-4 -ml-4 -mr-4 text-center text-white rounded-b bg-error">
						<strong>
							This service is eligible for reimbursement based on the
							client&apos;s enrollee status
						</strong>
					</div>
				)}
			</div>
			<div className="flex flex-row gap-4 py-4">
				<div className="flex-none bg-white rounded w-72">
					<ul className="menu">
						<li>
							<h2 className="menu-title">Application</h2>
							{serviceForms.isFetching && (
								<span className="loading loading-spinner loading-sm"></span>
							)}
							<ul>
								{serviceForms.isFetched && serviceForms?.data?.length === 0 && (
									<li>
										<em>No forms</em>
									</li>
								)}
								{serviceForms.isFetched &&
									serviceForms?.data?.map((form) => (
										<li key={`${form.id}`}>
											<a
												onClick={handleMenuClick}
												data-form-id={form.id}
												className={cn({ active: selectedForm == form.id })}
											>
												{form.name}
											</a>
										</li>
									))}
							</ul>
						</li>
					</ul>
				</div>
				<ModuleForm
					formId={selectedForm}
					clientId={serviceRequest.client_id}
					readOnly={!isEditable}
				/>
				<div className="flex flex-col flex-1 w-2/5 gap-4">
					<WorkNotesForm requestId={serviceRequest.id} readOnly={!isEditable} />
					{isEditable && <ReferralHistory requestId={serviceRequest.id} />}
				</div>
			</div>
			<dialog id="confirm-release" className="modal" ref={modalRef}>
				<div className="modal-box">
					<h3>
						Release to{' '}
						{
							serviceRequest?.external_211_service?.data.locations[0]
								.organization_name
						}
						?
					</h3>
					<p className="py-4">
						You will no longer be able to make changes to this application.
					</p>
					<div className="modal-action">
						<form method="dialog">
							<button className="btn">Cancel</button>
							<button
								className="ml-4 btn"
								onClick={handleStatusChange}
								data-to-status="new"
							>
								Release
							</button>
						</form>
					</div>
				</div>
			</dialog>
			{isReceivingAgency && (
				<>
					<ReassignModal
						isOpen={isReassignModalOpen}
						agencyId={user.coord_agency_id}
						currentCoordId={serviceRequest.assigned_to_coord_id}
						onCancel={() => setReassignModalOpen(false)}
						onReassign={reassignMutation.mutate}
					/>
					<CloseRequestModal
						isOpen={isCloseModalOpen}
						onCancel={() => setCloseModalOpen(false)}
						onClose={handleClose}
						closeStatus={nextCloseStatus}
					/>
					<SKUModal
						isOpen={isSKUModalOpen}
						onCancel={() => setSKUModalOpen(false)}
						currentSKUs={serviceRequest?.taxonomy_skus}
						availableSKUs={availableSKUsQuery?.data}
						onChangeSKUs={skuMutation.mutate}
					/>
					<InvoiceModal
						isOpen={isInvoiceModalOpen}
						requestId={requestId}
						onClose={() => setInvoiceModalOpen(false)}
					/>
					<FundsModal
						isOpen={isFundsModalOpen}
						requestId={requestId}
						onClose={() => setFundsModalOpen(false)}
					/>
				</>
			)}
			{!readOnly && (
				<>
					<CloneRequestModal
						isOpen={isCloneModalOpen}
						onCancel={() => setCloneModalOpen(false)}
						onClone={handleClone}
						client={serviceRequest.client}
					/>
					<ProfileModal
						isOpen={isProfileModalOpen}
						onClose={() => setProfileModalOpen(false)}
						client={serviceRequest?.client}
						onSaveAndClose={(client, skipClose) =>
							clientMutation.mutate([client, skipClose])
						}
					/>
				</>
			)}
		</>
	);
};

export default ApplicationModules;
